import React, { useState, useRef, useEffect } from 'react';
import useBreakpoint from '../../../hooks/useBreakpoint/index.js';
import styles from './categoryBar.module.scss.js';
import { CategoryBarButtonItem, CategoryBarItem } from '../CategoryBarItem/index.js';
import { AllCategoriesList } from '../AllCategoriesList/index.js';

/* eslint-disable camelcase */
function CategoryBar(_ref) {
  let {
    edition,
    tracking,
    setShowOverlay,
    showOverlay,
    pageType,
    topLevelNavItems,
    useUnitTracking,
    useClickTracking,
    trackClientContentAction,
    baseUrl,
    baseParamsString
  } = _ref;
  const [version] = useState(pageType === 'default' ? 'full' : 'mini');
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [showSubCategories, setShowSubCategories] = useState(false);
  const showSubCategoriesRef = useRef(false);
  const showAllCategoriesRef = useRef(false);
  const showViewAll = edition === 'en-us' || edition === 'en-ca';
  const isSmall = useBreakpoint('(max-width: 52rem)');
  const onMouseOver = link => {
    if (showAllCategories) {
      return;
    }
    setShowOverlay(true);
    showSubCategoriesRef.current = link;
    setShowSubCategories(link);
  };
  const onMouseLeave = () => {
    if (showAllCategories) {
      return;
    }
    showSubCategoriesRef.current = false;
    setTimeout(() => {
      if (showAllCategoriesRef.current) {
        return;
      }
      if (!showSubCategoriesRef.current) {
        setShowOverlay(false);
        setShowSubCategories(false);
      }
    }, 800); // delay before hide menu
  };

  const [categoryItems] = useState(() => {
    let items = topLevelNavItems.map((category, index) => {
      const subunitTrackingLayer = {
        ...tracking,
        subunit_type: 'component',
        subunit_name: 'category_bar',
        position_in_subunit: index + 1
      };
      let additionalEvents = {};
      if (pageType !== 'default' && category.children && category.children.length) {
        additionalEvents.onMouseOver = () => onMouseOver(category.link);
        additionalEvents.onMouseLeave = () => onMouseLeave(category.link);
      }
      return {
        ...category,
        full_link: category.full_link || false,
        subunitTrackingLayer: subunitTrackingLayer,
        additionalEvents: additionalEvents
      };
    });
    if (isSmall) {
      items.sort((a, b) => a.order_mobile - b.order_mobile);
    } else {
      items.sort((a, b) => a.order - b.order);
    }
    return items;
  });
  const viewAllTrackingLayer = {
    ...tracking,
    subunit_name: 'category_bar',
    subunit_type: 'component',
    item_name: 'viewAll',
    item_type: 'button',
    action_type: 'show',
    action_value: 'viewAll'
  };
  const showAll = () => {
    if (showAllCategories) {
      setShowOverlay(false);
    } else {
      trackClientContentAction({}, viewAllTrackingLayer);
      setShowOverlay(true);
    }
    setShowAllCategories(!showAllCategories);
  };
  useEffect(() => {
    if (!showOverlay && (showAllCategories || showSubCategories)) {
      setShowAllCategories(false);
      setShowSubCategories(false);
    }
  }, [showOverlay]);
  useEffect(() => {
    showAllCategoriesRef.current = showAllCategories;
  }, [showAllCategories]);
  return /*#__PURE__*/React.createElement("div", {
    className: `${styles.categoryBar} ${styles[edition]} ${styles[version]} ${pageType !== 'default' ? styles.categoryPage : ''}`
  }, /*#__PURE__*/React.createElement("div", {
    className: `${styles.categoryWrapper}`
  }, categoryItems.length > 0 && categoryItems.map((item, index) => {
    if (item.name === 'Subscriptions') {
      return [];
    }
    return /*#__PURE__*/React.createElement(CategoryBarItemWrapper, {
      key: `cbi-${index}`,
      showAllCategories: showAllCategories,
      showSubCategories: showSubCategories,
      index: index,
      item: item,
      version: version,
      items: categoryItems,
      tracking: tracking,
      topLevelNavItems: topLevelNavItems,
      pageType: pageType,
      useClickTracking: useClickTracking,
      useUnitTracking: useUnitTracking,
      trackClientContentAction: trackClientContentAction,
      baseUrl: baseUrl,
      baseParamsString: baseParamsString
    });
  }), categoryItems.length > 0 && pageType === 'default' && showViewAll && /*#__PURE__*/React.createElement("div", {
    key: "cbi-va",
    className: `${styles.item} ${styles.itemViewAll}`
  }, /*#__PURE__*/React.createElement(CategoryBarButtonItem, {
    version: version,
    name: "View All",
    link: `${showAllCategories ? 'viewAllDark' : 'viewAll'}`,
    onClick: showAll,
    isActive: showAllCategories,
    useUnitTracking: useUnitTracking
  }))), /*#__PURE__*/React.createElement("div", {
    className: styles.categoriesOverlay
  }), categoryItems.length > 0 && pageType !== 'default' && showViewAll && /*#__PURE__*/React.createElement("div", {
    key: "cbi-va",
    className: `${styles.item} ${styles.itemViewAll}`
  }, /*#__PURE__*/React.createElement(CategoryBarButtonItem, {
    version: version,
    name: "View All",
    link: `${showAllCategories ? 'viewAllDark' : 'viewAll'}`,
    onClick: showAll,
    isActive: showAllCategories,
    useUnitTracking: useUnitTracking
  })), showAllCategories && /*#__PURE__*/React.createElement(AllCategoriesList, {
    tracking: tracking,
    items: categoryItems,
    topLevelNavItems: topLevelNavItems,
    pageType: pageType,
    edition: edition,
    useClickTracking: useClickTracking,
    baseUrl: baseUrl
  }));
}
function CategoryBarItemWrapper(_ref2) {
  let {
    showAllCategories,
    showSubCategories,
    index,
    item,
    version,
    tracking,
    items,
    pageType,
    useClickTracking,
    useUnitTracking,
    trackClientContentAction,
    baseUrl,
    baseParamsString
  } = _ref2;
  const viewSubcategoriesTrackingLayer = {
    ...tracking,
    subunit_name: 'category_bar',
    subunit_type: 'component',
    item_name: item.name,
    item_type: 'button',
    action_type: 'show',
    action_value: 'viewSubcategories'
  };
  const onMouseOver = () => {
    if (item.additionalEvents.onMouseOver && !showAllCategories) {
      item.additionalEvents.onMouseOver(item.link);
      trackClientContentAction({}, viewSubcategoriesTrackingLayer);
    }
  };
  const onMouseLeave = () => {
    if (item.additionalEvents.onMouseLeave) {
      item.additionalEvents.onMouseLeave();
    }
  };
  return /*#__PURE__*/React.createElement("div", {
    className: styles.item,
    onMouseLeave: onMouseLeave,
    onMouseEnter: onMouseOver
  }, /*#__PURE__*/React.createElement(CategoryBarItem, {
    version: version,
    name: item.name,
    link: item.link,
    tracking: item.subunitTrackingLayer,
    pageType: pageType,
    useUnitTracking: useUnitTracking,
    baseUrl: baseUrl,
    baseParamsString: baseParamsString
  }), showSubCategories === item.link && /*#__PURE__*/React.createElement(SubCategoriesList, {
    key: `scl-${index}`,
    link: item.link,
    tracking: tracking,
    items: items,
    useClickTracking: useClickTracking,
    baseUrl: baseUrl
  }));
}
function SubCategoriesList(_ref3) {
  let {
    link,
    tracking,
    items,
    useClickTracking,
    baseUrl
  } = _ref3;
  const category = items.find(element => element.link === link);
  if (!category?.children?.length) {
    return '';
  }
  let href = category.link.includes('giftguide') ? category.link : `${baseUrl}/${category.link}`;
  if (category.full_link) {
    href = category.link;
  }
  const children = category.children.sort((a, b) => {
    if (a.name < b.name) {
      return -1;
    }
    if (a.name > b.name) {
      return 1;
    }
    return 0;
  });
  const columnItems = children.reduce((columns, item) => {
    const parentHref = href.split('?')[0];
    const maxItemInColumn = 5;
    // max count items in column = 5
    if (columns[columns.length - 1].length < maxItemInColumn) {
      columns[columns.length - 1].push({
        link: `${parentHref}/${item.link}`,
        name: item.name
      });
    } else {
      columns.push([{
        link: `${parentHref}/${item.link}`,
        name: item.name
      }]);
    }
    return columns;
  }, [[{
    link: href,
    name: 'All ' + category.name,
    additionalClass: styles.subCategoriesListTitle
  }]]);
  return /*#__PURE__*/React.createElement("div", {
    className: styles.subCategoriesListWrapper
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.subCategoriesList
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.subCategoriesListContainerWrapper
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.subCategoriesListContainer
  }, columnItems.map((column, index) => {
    return /*#__PURE__*/React.createElement("div", {
      key: `sclc-${index}`,
      className: styles.subCategoriesListColumn
    }, column.map((item, itemIndex) => {
      return /*#__PURE__*/React.createElement(SubCategoriesChild, {
        key: `scc-${index}-${itemIndex}`,
        item: item,
        tracking: tracking,
        index: itemIndex,
        useClickTracking: useClickTracking
      });
    }));
  })))));
}
function SubCategoriesChild(_ref4) {
  let {
    item,
    tracking,
    index,
    useClickTracking
  } = _ref4;
  const ref = useClickTracking({
    ...tracking,
    subunit_type: 'component',
    subunit_name: 'sub_categories_list',
    position_in_subunit: index + 1,
    item_type: 'text',
    item_name: item.name,
    target_content_type: 'url',
    target_content_id: item.link,
    target_content_url: item.link
  });
  return /*#__PURE__*/React.createElement("a", {
    ref: ref,
    href: item.link,
    className: `${styles.childItem} ${item.additionalClass}`
  }, item.name);
}

export { CategoryBar, CategoryBar as default };
